import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const { login, loginWithGoogle } = useAuth();
    const navigate = useNavigate();

    // Consume any pending OIDC redirect saved by OidcAuthorizePage.
    // We use window.location.replace (not SPA navigate) because the /login
    // route is wrapped by <PublicRoute> which auto-redirects authenticated
    // users to "/" on next render, racing and overriding a SPA navigate here.
    const consumePostLoginRedirect = () => {
        try {
            const saved = localStorage.getItem('post_login_redirect');
            localStorage.removeItem('post_login_redirect');
            if (
                saved &&
                saved.startsWith('/') &&
                !saved.startsWith('//') &&
                !saved.startsWith('/login') &&
                !saved.startsWith('/register')
            ) {
                window.location.replace(saved);
                return true;
            }
        } catch (e) { /* noop */ }
        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success('Welcome back!');
            if (!consumePostLoginRedirect()) navigate('/');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid email or password');
        } finally {
            setLoading(false);
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const handleGoogleSuccess = async (credentialResponse) => {
        setGoogleLoading(true);
        try {
            await loginWithGoogle(credentialResponse.credential);
            toast.success('Welcome!');
            if (!consumePostLoginRedirect()) navigate('/');
        } catch (error) {
            console.error('Google login error:', error);
            toast.error(error.response?.data?.detail || 'Failed to login with Google');
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleError = () => {
        toast.error('Google login failed. Please try again.');
    };

    return (
        <div className="auth-page">
            <div className="auth-left">
                <div className="auth-left-content">
                    <div className="mb-8">
                        <img
                            src="/assets/logo-white.png"
                            alt="TaskGlory"
                            className="h-12 w-auto mb-3"
                        />
                        <p className="text-[#A7E3C8] text-lg">Organize. Track. Achieve.</p>
                    </div>
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                            <div>
                                <h3 className="font-semibold mb-1">Flexible Hierarchy</h3>
                                <p className="text-[#EAF4F1]/80 text-sm">Workspaces, Spaces, Folders, Lists & unlimited subtasks</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                            <div>
                                <h3 className="font-semibold mb-1">Custom Fields</h3>
                                <p className="text-[#EAF4F1]/80 text-sm">Add any field type to track what matters to you</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                            <div>
                                <h3 className="font-semibold mb-1">Time Tracking</h3>
                                <p className="text-[#EAF4F1]/80 text-sm">Built-in timer, manual logs, and billable hours</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="auth-right">
                <div className="w-full max-w-sm">
                    <div className="mb-8">
                        <h2 className="font-heading text-2xl font-bold text-brand-text-primary mb-2">Welcome back</h2>
                        <p className="text-brand-text-secondary">Sign in to your account</p>
                    </div>

                    {/* Google Login Button */}
                    <div className="mb-6">
                        <div className="flex justify-center" data-testid="google-login-btn">
                            {googleLoading ? (
                                <div className="flex items-center gap-2 py-2 px-4 bg-gray-100 rounded-lg">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm text-gray-600">Signing in...</span>
                                </div>
                            ) : (
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={handleGoogleError}
                                    text="signin_with"
                                    shape="rectangular"
                                    theme="outline"
                                    size="large"
                                    width="320"
                                />
                            )}
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-brand-border"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-brand-text-muted">atau login dengan email</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-brand-text-primary font-medium">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="bg-brand-background border-brand-border focus:ring-[#2F6F63]/30"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-brand-text-primary font-medium">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="bg-brand-background border-brand-border focus:ring-[#2F6F63]/30 pr-10"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                            <div className="flex justify-end">
                                <Link
                                    to="/forgot-password"
                                    className="text-xs text-brand-primary hover:underline"
                                    data-testid="forgot-password-link"
                                >
                                    Lupa password?
                                </Link>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white h-11"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Sign In
                        </Button>
                    </form>
                    <p className="mt-6 text-center text-sm text-brand-text-secondary">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-brand-primary font-medium hover:underline" data-testid="register-link">
                            Create one
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
